import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Layout from '../../components/Layout';
import Loading from '../../components/Loading';
import { useToast } from '../../components/Toast';
import {
  fetchCustomers,
  setSearchQuery,
  setCurrentPage,
  createCustomer,
  toggleCustomerStatus,
  deleteCustomer,
  upsertCustomerFromSocket,
  updateCustomerStatusFromSocket,
  markCustomerRemovedFromSocket,
} from '../../store/slices/customerSlice';
import { useClientSocket } from '../../hooks/useSocketListeners';
import './Customers.css';

const ITEMS_PER_PAGE = 9;

const Customers = () => {
  const dispatch = useDispatch();
  const toast = useToast();
  const { customers, searchQuery, currentPage, isLoading } = useSelector((state) => state.customer);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    storeName: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    dispatch(fetchCustomers());
  }, []);

  const handleClientSocketEvent = useCallback(
    (e) => {
      const action = e?.action;
      const payload = e?.clientData ?? e?.data;

      if (action === 'created') {
        if (payload) dispatch(upsertCustomerFromSocket(payload));
        return;
      }

      const id = payload?.id;
      if (id == null) return;

      if (action === 'blocked') {
        // Để đảm bảo đồng bộ tuyệt đối giữa nhiều admin,
        // luôn refetch danh sách khi có thay đổi trạng thái.
        dispatch(fetchCustomers());
        return;
      }

      if (action === 'unblocked') {
        dispatch(fetchCustomers());
        return;
      }

      if (action === 'deleted') {
        dispatch(markCustomerRemovedFromSocket({ id }));
        return;
      }

      // Fallback
      dispatch(fetchCustomers());
    },
    [dispatch]
  );

  useClientSocket(handleClientSocketEvent);

  const totalPages = Math.ceil(customers.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentData = customers.slice(startIndex, endIndex);

  const handleSearch = () => {
    dispatch(fetchCustomers(searchQuery));
  };

  const handleCreateNew = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setFormData({ email: '', storeName: '' });
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmitCreate = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.storeName) return;

    setIsSubmitting(true);
    try {
      await dispatch(createCustomer({
        clientId: `client_${Date.now()}`,
        email: formData.email,
        storeName: formData.storeName,
      })).unwrap();
      handleCloseModal();
      toast.success('クライアントを作成しました');
    } catch (error) {
      toast.error(error||'クライアントの作成に失敗しました');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleStatus = async (customerId, currentStatus) => {
    const isStopping = currentStatus !== '停止中';
    const confirmMessage = isStopping
      ? 'このクライアントを「停止中」に変更してもよろしいですか？'
      : 'このクライアントの停止を解除してもよろしいですか？';

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      await dispatch(toggleCustomerStatus({ customerId, currentStatus })).unwrap();
      const newStatus = currentStatus === '停止中' ? '有効' : '停止中';
      toast.success(`アカウント状態を「${newStatus}」に変更しました`);
    } catch (error) {
      toast.error('状態の変更に失敗しました');
    }
  };

  const handleDelete = async (customerId) => {
    if (window.confirm('このお客様を削除してもよろしいですか？')) {
      try {
        await dispatch(deleteCustomer(customerId)).unwrap();
        toast.success('クライアントを削除しました');
      } catch (error) {
        toast.error('削除に失敗しました');
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      dispatch(setCurrentPage(page));
    }
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        pages.push(1, 2, 3, '...', totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1, '...', totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, '...', currentPage, '...', totalPages);
      }
    }

    return pages;
  };

  const getStatusClass = (status) => {
    switch (status) {
      case '有効':
        return 'customers__status--active';
      case '停止中':
        return 'customers__status--stopped';
      case '削除済み':
        return 'customers__status--deleted';
      default:
        return '';
    }
  };

  return (
    <Layout>
      <div className="customers">
        <div className="customers__card">
          <h1 className="customers__title">クライアント</h1>
          
          <div className="customers__scroll-area">
          <div className="customers__create-btn-container">
            <button className="customers__create-btn" onClick={handleCreateNew}>
              新規作成
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="customers__create-btn-icon">
                <path d="M5.25 6.375a4.125 4.125 0 1 1 8.25 0 4.125 4.125 0 0 1-8.25 0ZM2.25 19.125a7.125 7.125 0 0 1 14.25 0v.003l-.001.119a.75.75 0 0 1-.363.63 13.067 13.067 0 0 1-6.761 1.873c-2.472 0-4.786-.684-6.76-1.873a.75.75 0 0 1-.364-.63l-.001-.122ZM18.75 7.5a.75.75 0 0 0-1.5 0v2.25H15a.75.75 0 0 0 0 1.5h2.25v2.25a.75.75 0 0 0 1.5 0v-2.25H21a.75.75 0 0 0 0-1.5h-2.25V7.5Z" />
              </svg>
            </button>
          </div>

          <div className="customers__filters">
            <div className="customers__filter-row">
              <input
                type="text"
                className="customers__filter-input"
                placeholder="店名で検索"
                value={searchQuery}
                onChange={(e) => dispatch(setSearchQuery(e.target.value))}
                onKeyPress={handleKeyPress}
              />
              <button 
                className="customers__search-btn" 
                onClick={handleSearch}
                disabled={isLoading}
              >
                検索
              </button>
            </div>
          </div>

          {isLoading && <Loading text="顧客データを読み込み中..." />}

          {!isLoading && (
            <div className="customers__table-wrapper">
              <table className="customers__table">
                <thead className="customers__table-head">
                  <tr className="customers__table-row">
                    <th className="customers__table-th">ID</th>
                    <th className="customers__table-th">Email</th>
                    <th className="customers__table-th">店</th>
                    <th className="customers__table-th">アカウント状態</th>
                    <th className="customers__table-th customers__table-th--actions">操作</th>
                  </tr>
                </thead>
                <tbody className="customers__table-body">
                  {currentData.map((customer) => (
                    <tr key={customer.id} className="customers__table-row">
                      <td className="customers__table-td">{customer.id}</td>
                      <td className="customers__table-td">{customer.email}</td>
                      <td className="customers__table-td">{customer.storeName}</td>
                      <td className="customers__table-td">
                        <span className={`customers__status ${getStatusClass(customer.accountStatus)}`}>
                          {customer.accountStatus}
                        </span>
                      </td>
                      <td className="customers__table-td customers__table-td--actions">
                        {customer.accountStatus !== '削除済み' && (
                          <div className="customers__actions">
                            <button
                              className={`customers__action-btn ${
                                customer.accountStatus === '停止中' 
                                  ? 'customers__action-btn--unlock' 
                                  : 'customers__action-btn--stop'
                              }`}
                              onClick={() => handleToggleStatus(customer.id, customer.accountStatus)}
                            >
                              {customer.accountStatus === '停止中' ? '解除' : '停止'}
                            </button>
                            {/* <button
                              className="customers__action-btn customers__action-btn--delete"
                              onClick={() => handleDelete(customer.id)}
                            >
                              削除
                            </button> */}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!isLoading && customers.length === 0 && (
            <div className="customers__empty">
              <p>データがありません</p>
            </div>
          )}

          {!isLoading && customers.length > 0 && (
            <div className="customers__pagination">
              <button
                className="customers__pagination-btn customers__pagination-btn--nav"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="10 " height="10">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
                </svg>
              </button>

              {renderPagination().map((page, index) => (
                <button
                  key={index}
                  className={`customers__pagination-btn ${
                    page === currentPage ? 'customers__pagination-btn--active' : ''
                  } ${page === '...' ? 'customers__pagination-btn--dots' : ''}`}
                  onClick={() => page !== '...' && handlePageChange(page)}
                  disabled={page === '...'}
                >
                  {page}
                </button>
              ))}

              <button
                className="customers__pagination-btn customers__pagination-btn--nav"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="10" height="10">
                  <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
                </svg>
              </button>
            </div>
          )}
          </div>
        </div>

        {isModalOpen && (
          <div className="customers__modal-overlay" onClick={handleCloseModal}>
            <div className="customers__modal" onClick={(e) => e.stopPropagation()}>
              <div className="customers__modal-header">
                <h2 className="customers__modal-title">クライアントID発行</h2>
                <button className="customers__modal-close" onClick={handleCloseModal}>
                  ×
                </button>
              </div>
              <form className="customers__modal-form" onSubmit={handleSubmitCreate}>
                <div className="customers__modal-field">
                  <label className="customers__modal-label" htmlFor="email">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    className="customers__modal-input"
                    placeholder="クライアントID"
                    value={formData.email}
                    onChange={handleFormChange}
                    disabled={isSubmitting}
                    required
                  />
                </div>
                <div className="customers__modal-field">
                  <label className="customers__modal-label" htmlFor="storeName">
                    店舗名等
                  </label>
                  <input
                    type="text"
                    id="storeName"
                    name="storeName"
                    className="customers__modal-input"
                    placeholder="店舗名等"
                    value={formData.storeName}
                    onChange={handleFormChange}
                    disabled={isSubmitting}
                    required
                  />
                </div>
                <div className="customers__modal-actions">
                  <button
                    type="submit"
                    className="customers__modal-submit"
                    disabled={isSubmitting || !formData.email || !formData.storeName}
                  >
                    {isSubmitting ? '作成中...' : '新規作成'}
                    {!isSubmitting && (
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="customers__modal-submit-icon">
                        <path fillRule="evenodd" d="M12 3.75a.75.75 0 0 1 .75.75v6.75h6.75a.75.75 0 0 1 0 1.5h-6.75v6.75a.75.75 0 0 1-1.5 0v-6.75H4.5a.75.75 0 0 1 0-1.5h6.75V4.5a.75.75 0 0 1 .75-.75Z" clipRule="evenodd" />
                      </svg>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Customers;
